import { Component, type ErrorInfo, type ReactNode } from 'react';

type Props = { children: ReactNode; fallback?: ReactNode };
type State = { error: Error | null };

/**
 * Keeps one bad block from blanking the whole site.
 *
 * The error is still reported to the console, loudly: a render failure here
 * means content is not reaching the page, which is never acceptable silently.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[health-prime] render failed', error, info.componentStack);
  }

  componentDidUpdate(prev: Props): void {
    if (prev.children !== this.props.children && this.state.error) {
      this.setState({ error: null });
    }
  }

  render(): ReactNode {
    if (this.state.error) {
      return (
        this.props.fallback ?? <div className="hp-error" role="alert" data-error="render" />
      );
    }
    return this.props.children;
  }
}
